import { useCallback, useEffect, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { Loader2, Upload } from "lucide-react";

import { HelpLink } from "../help/links";
import { formatFileSize, validateFile } from "../lib/files";
import {
  DRAFT_ACCEPT,
  DRAFT_STATUS_HINT,
  MAX_DRAFT_FILES,
  canRetryParsing,
  draftStatusLabel,
  fetchDrafts,
  isSettled,
  isWithdrawable,
  retryDraft,
  uploadDraft,
  withdrawDraft,
  type AiAvailability,
  type DraftStatus,
  type DraftView,
} from "../lib/drafts";
import { formatDateTime } from "../lib/format";
import { InWorkStamp } from "../components/InWorkStamp";
import { useAuth } from "../auth/AuthContext";
import { DemoModeBanner } from "../components/DemoModeBanner";

/**
 * Экран «Регистрация» (E11): отправка комплекта на ИИ-разбор и состояние очереди.
 *
 * ТРИ РЕШЕНИЯ, КОТОРЫЕ ЗДЕСЬ ВИДНЫ.
 *
 * 1. ВЫКЛЮЧЕННЫЙ РАЗБОР — ШТАТНОЕ СОСТОЯНИЕ, А НЕ ПОЛОМКА (план §3.12). До подтверждённого
 *    платного тарифа провайдера разбор выключен и на площадке, и в проде. Поэтому здесь нет
 *    кнопки, за которой отказ: форма не показывается вовсе, а на её месте — прямой текст, что
 *    разбора нет, почему и как завести договор обычным путём. Кнопка, отвечающая ошибкой, —
 *    это обещание, которого система не держит.
 *
 * 2. СОСТОЯНИЯ — СЛОВАМИ. Ни `processing`, ни «статус 2»: делопроизводитель читает ровно то,
 *    что написано в его памятке (docs/09).
 *
 * 3. ПОВТОР — ДЕЙСТВИЕ ЧЕЛОВЕКА, А НЕ АВТОМАТИКА. Кнопка есть только у состояния «ошибка» и
 *    просит подтверждения: часть отказов повтором лечится («высокий спрос»), часть — нет
 *    («регион не поддерживается»), и решать должен тот, кто видит причину.
 *
 * Опрос идёт, только пока в очереди есть черновик «в обработке», и прекращается сам: держать
 * запрос раз в три секунды ради списка, который больше не меняется, незачем.
 */

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly drafts: DraftView[]; readonly ai: AiAvailability }
  | { readonly kind: "error"; readonly message: string };

/** Как часто спрашиваем о ходе разбора. Спайк: 19–73 секунды на документ. */
const POLL_MS = 3000;

const btnPrimary =
  "rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface disabled:opacity-50";
const btnSecondary =
  "rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg disabled:opacity-50";

/** Оформление штампа состояния — токенами docs/07 (правило 9), без «сырых» цветов. */
const STATUS_CHIP: Record<DraftStatus, string> = {
  processing: "border-border bg-bg text-muted",
  ready: "border-done-fg/40 bg-done-bg text-done-fg",
  failed: "border-overdue-fg/40 bg-overdue-bg text-overdue-fg",
  confirmed: "border-done-fg/40 bg-done-bg text-done-fg",
  rejected: "border-border bg-bg text-muted",
  withdrawn: "border-border bg-bg text-muted",
};

export function RegisterPage() {
  const { user } = useAuth();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [selected, setSelected] = useState<File[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const result = await fetchDrafts();
    if (!result.ok) {
      setState({ kind: "error", message: result.message });
      return;
    }
    setState({ kind: "ready", drafts: result.value.drafts, ai: result.value.ai });
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Опрос — только пока есть что ждать. Как только все черновики в конечном состоянии, таймер
  // снимается: иначе экран, открытый на весь день, стучится в сервер без всякой пользы.
  const waiting = state.kind === "ready" && state.drafts.some((d) => !isSettled(d.status));
  useEffect(() => {
    if (!waiting) return;
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [waiting, load]);

  /**
   * Отбор файлов — ОДНА проверка на оба пути (кнопка и перетаскивание).
   *
   * Разведённые копии здесь разошлись бы молча и в худшую сторону: перетаскивание — путь, которым
   * пользуются чаще, и именно на нём проверка типа и размера пропала бы незамеченной до того
   * дня, когда сервер откажет уже после загрузки.
   */
  const accept = useCallback((files: readonly File[]): void => {
    setFormError(null);
    setNotice(null);
    if (files.length === 0) return;
    if (files.length > MAX_DRAFT_FILES) {
      setFormError(`За раз можно отправить не больше ${MAX_DRAFT_FILES} файлов.`);
      setSelected([]);
      return;
    }
    for (const file of files) {
      const check = validateFile(file);
      if (!check.ok) {
        setFormError(`«${file.name}»: ${check.message}`);
        setSelected([]);
        return;
      }
    }
    setSelected([...files]);
  }, []);

  function onPick(event: ChangeEvent<HTMLInputElement>): void {
    accept(Array.from(event.target.files ?? []));
  }

  async function onSend(): Promise<void> {
    if (selected.length === 0) return;
    setBusy(true);
    setFormError(null);
    const result = await uploadDraft(selected);
    setBusy(false);
    if (!result.ok) {
      setFormError(result.message);
      return;
    }
    setSelected([]);
    if (inputRef.current) inputRef.current.value = "";
    setNotice("Комплект отправлен на разбор. Состояние появится в списке ниже.");
    await load();
  }

  async function onRetry(draft: DraftView): Promise<void> {
    // Подтверждение не формальность: повторный запрос к модели стоит денег, и нажать кнопку
    // случайно на списке из десятка строк слишком легко.
    const agreed = window.confirm(
      "Повторить разбор? Это новый платный запрос к модели.\n\n" +
        "Повтор помогает, если отказ был временным («высокий спрос», обрыв связи). " +
        "Если причина в настройках или доступе, повтор её не исправит.",
    );
    if (!agreed) return;
    setBusy(true);
    const result = await retryDraft(draft.id);
    setBusy(false);
    if (!result.ok) {
      setFormError(result.message);
      return;
    }
    setNotice("Разбор запущен заново.");
    await load();
  }

  /**
   * ОТЗЫВ СВОЕГО КОМПЛЕКТА (D-24, пункт «в»).
   *
   * До этой задачи отправитель, загрузивший не тот файл, не мог сделать ничего: комплект уходил
   * юристу и оставался в очереди, пока тот его не отклонит. Отзыв возможен, пока черновик не взят
   * юристом в работу; кто и что вправе отозвать, решает сервер (правило 5).
   */
  async function onWithdraw(draft: DraftView, reason: string): Promise<void> {
    setBusy(true);
    setFormError(null);
    const result = await withdrawDraft(draft.id, reason);
    setBusy(false);
    if (!result.ok) {
      setFormError(result.message);
      return;
    }
    setNotice("Комплект отозван. Файлы сохранены, юристу он больше не показывается.");
    await load();
  }

  if (state.kind === "loading") {
    return (
      <section aria-labelledby="register-title" aria-busy="true">
        <h1 id="register-title" className="text-[20px] font-bold text-ink">
          Регистрация
        </h1>
        <p className="mt-4 text-muted">Загрузка…</p>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section aria-labelledby="register-title">
        <h1 id="register-title" className="text-[20px] font-bold text-ink">
          Регистрация
        </h1>
        <p role="alert" className="mt-4 text-overdue-fg">
          {state.message}
        </p>
      </section>
    );
  }

  return (
    <section aria-labelledby="register-title">
      <h1 id="register-title" className="text-[20px] font-bold text-ink">
        Регистрация
      </h1>

      <DemoModeBanner notice={state.ai.demoNotice} />

      {state.ai.enabled ? (
        <UploadForm
          selected={selected}
          busy={busy}
          error={formError}
          notice={notice}
          inputRef={inputRef}
          onPick={onPick}
          onDrop={accept}
          onClear={() => {
            setSelected([]);
            if (inputRef.current) inputRef.current.value = "";
          }}
          onSend={onSend}
        />
      ) : (
        <AiDisabled reason={state.ai.disabledReason} />
      )}

      <h2 className="mt-8 text-[15px] font-bold text-ink">Отправленные комплекты</h2>
      {state.drafts.length === 0 ? (
        <p className="mt-2 text-[13px] text-muted">
          Пока ничего не отправлено. Здесь появятся комплекты и ход их разбора.
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {state.drafts.map((draft) => (
            <DraftRow
              key={draft.id}
              draft={draft}
              busy={busy}
              mine={draft.createdById === (user?.id ?? null)}
              onRetry={onRetry}
              onWithdraw={onWithdraw}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/** Честное состояние выключенного разбора: без кнопки, с объяснением и обычным путём. */
function AiDisabled({ reason }: { reason: string }) {
  return (
    <div
      role="status"
      className="mt-4 rounded-[var(--radius-l)] border border-border bg-surface p-5"
    >
      <p className="text-[15px] font-bold text-ink">ИИ-разбор не включён</p>
      <p className="mt-2 max-w-[var(--w-text)] text-[14px] text-muted">{reason}</p>
      <Link to="/contracts/new" className={`${btnSecondary} mt-4 inline-block`}>
        Новый договор
      </Link>
    </div>
  );
}

interface UploadFormProps {
  readonly selected: File[];
  readonly busy: boolean;
  readonly error: string | null;
  readonly notice: string | null;
  readonly inputRef: React.RefObject<HTMLInputElement | null>;
  readonly onPick: (event: ChangeEvent<HTMLInputElement>) => void;
  readonly onDrop: (files: readonly File[]) => void;
  readonly onClear: () => void;
  readonly onSend: () => Promise<void>;
}

/**
 * Отправка комплекта: заметная кнопка выбора и область перетаскивания (постановка 16.08.2026).
 *
 * ЧТО БЫЛО НЕ ТАК. Стоял голый `<input type="file">` — «Выбрать файлы / Файл не выбран». Это
 * системный элемент: он выглядит одинаково в форме отправки договора на разбор и в любой другой
 * форме мира, не объясняет, что от человека хотят, и не даёт перетащить файл — а сканы приходят
 * из папки, и перетащить их естественнее, чем искать в диалоге.
 *
 * Сам `<input>` остаётся — он спрятан визуально, но НЕ убран: на нём держится и работа с
 * клавиатуры, и подпись поля, и выбор через диалог. Подменять его кнопкой без input значило бы
 * сделать форму недоступной для тех, кто не пользуется мышью (ТЗ 4.1.4).
 */
function UploadForm(props: UploadFormProps) {
  const { selected, busy, error, notice, inputRef, onPick, onDrop, onClear, onSend } = props;
  const [over, setOver] = useState(false);

  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setOver(false);
    if (busy) return;
    onDrop(Array.from(event.dataTransfer.files ?? []));
  }

  return (
    <div className="mt-4 max-w-[var(--w-form)] rounded-[var(--radius-l)] border border-border bg-surface p-5">
      <p className="max-w-[var(--w-text)] text-[14px] text-muted">
        Приложите договор и спецификации к нему{" "}
        <strong className="text-ink">одним комплектом</strong> — модель смотрит документы вместе и
        замечает расхождения между ними. Разбор идёт в фоне, обычно меньше минуты; ждать на этой
        странице не обязательно.
      </p>

      <label className="mt-4 block text-[13px] font-bold text-ink" htmlFor="draft-files">
        Файлы комплекта (PDF, JPG, PNG, DOCX; до {MAX_DRAFT_FILES} файлов)
      </label>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={handleDrop}
        className={`mt-1.5 flex flex-col items-center gap-2 rounded-[var(--radius-l)] border-2 border-dashed p-6 text-center ${
          over ? "border-accent bg-control-bg" : "border-border bg-bg"
        }`}
      >
        <Upload size={22} aria-hidden="true" className="text-muted" />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className={btnPrimary}
        >
          Выбрать файлы
        </button>
        <p className="text-[13px] text-muted">или перетащите файлы сюда</p>
        {/* Видимый для клавиатуры и скринридера, невидимый для глаза: `hidden` его бы отключил. */}
        <input
          id="draft-files"
          ref={inputRef}
          type="file"
          multiple
          accept={DRAFT_ACCEPT}
          onChange={onPick}
          disabled={busy}
          className="sr-only"
        />
      </div>

      {selected.length > 0 && (
        <div className="mt-3">
          <p className="text-[13px] font-bold text-ink">
            Выбрано файлов: {selected.length} — уйдут одним комплектом
          </p>
          <ul className="mt-1 space-y-1 text-[13px] text-muted">
            {selected.map((file) => (
              <li key={`${file.name}-${file.size}`}>
                {file.name} — <span className="num">{formatFileSize(file.size)}</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={onClear}
            disabled={busy}
            className="mt-2 text-[13px] font-bold text-accent underline disabled:opacity-50"
          >
            Очистить список
          </button>
        </div>
      )}

      {error !== null && (
        <p role="alert" className="mt-3 text-[13px] text-overdue-fg">
          {error}
        </p>
      )}
      {notice !== null && (
        <p role="status" className="mt-3 text-[13px] text-done-fg">
          {notice}
        </p>
      )}

      <button
        type="button"
        onClick={() => void onSend()}
        disabled={busy || selected.length === 0}
        className={`${btnPrimary} mt-4`}
      >
        {busy ? "Отправка…" : "Отправить на разбор"}
      </button>
    </div>
  );
}

/** Разбор состоялся: только тогда отметки об отправке файлов что-то значат. */
function parsed(status: DraftStatus): boolean {
  return status === "ready" || status === "confirmed" || status === "rejected";
}

interface DraftRowProps {
  readonly draft: DraftView;
  readonly busy: boolean;
  /** Комплект отправил ЭТОТ пользователь: отзывать своё вправе только он (D-24). */
  readonly mine: boolean;
  readonly onRetry: (draft: DraftView) => Promise<void>;
  readonly onWithdraw: (draft: DraftView, reason: string) => Promise<void>;
}

function DraftRow({ draft, busy, mine, onRetry, onWithdraw }: DraftRowProps) {
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState("");
  const names = draft.files.map((f) => f.originalName).join(", ");
  // Кнопка показывается ровно там, где отзыв пройдёт: своё, ещё не решённое и не взятое юристом
  // в работу. Настоящий запрет держит сервер — здесь мы лишь не обещаем невозможного.
  const canWithdraw = mine && isWithdrawable(draft.status) && draft.inWorkById === null;
  return (
    <li className="rounded-[var(--radius-l)] border border-border bg-surface p-4">
      <div className="flex flex-wrap items-center gap-2">
        {/* Состояние объявляется вслух при изменении: экран сам меняется в фоне, и без этого
            пользователь со скринридером узнавал бы о готовности только повторным обходом. */}
        <span
          role="status"
          aria-live="polite"
          className={`inline-flex items-center gap-1.5 rounded-[var(--radius-s)] border px-2 py-0.5 text-[12px] font-bold ${STATUS_CHIP[draft.status]}`}
        >
          {/*
            ВРАЩАЮЩИЙСЯ ИНДИКАТОР У «В ОБРАБОТКЕ» (постановка 16.08.2026). Статус меняется сам,
            фоном — опрос идёт раз в три секунды и сам прекращается, когда ждать больше нечего.
            Но пока он не сменился, неподвижный штамп «В обработке» неотличим от зависшего
            экрана: человек не знает, идёт ли работа, и жмёт «обновить». Движение — единственное,
            что отвечает на этот вопрос без слов.

            `aria-hidden` намеренно: незрячему пользователю про ход разбора говорит сам текст
            состояния и пояснение под ним, а крутящаяся картинка не сообщает ничего.
          */}
          {draft.status === "processing" && (
            <Loader2 size={12} aria-hidden="true" className="animate-spin" />
          )}
          {draftStatusLabel(draft)}
        </span>
        <span className="text-[13px] text-muted">
          {formatDateTime(draft.createdAt)}
          {draft.createdByName === null ? "" : `, ${draft.createdByName}`}
        </span>
        {draft.attempts > 1 && (
          <span className="text-[12px] text-muted">попыток: {draft.attempts}</span>
        )}
      </div>

      <p className="mt-2 text-[14px] text-ink">{names}</p>
      <p className="mt-1 text-[13px] text-muted">{DRAFT_STATUS_HINT[draft.status]}</p>

      {draft.status === "failed" && draft.errorText !== null && (
        <p className="mt-2 max-w-[var(--w-text)] text-[13px] text-overdue-fg">{draft.errorText}</p>
      )}

      {draft.status === "rejected" && draft.rejectReason !== null && (
        <p className="mt-2 max-w-[var(--w-text)] text-[13px] text-muted">
          Причина отклонения: {draft.rejectReason}
        </p>
      )}

      {draft.status === "withdrawn" && draft.withdrawReason !== null && (
        <p className="mt-2 max-w-[var(--w-text)] text-[13px] text-muted">
          Отозвал {draft.withdrawnByName ?? "—"}: {draft.withdrawReason}
        </p>
      )}

      {/* «В работе — X» видно и отправителю: отсюда понятно, почему кнопки отзыва больше нет и
          с кем разговаривать, если комплект отправлен по ошибке. */}
      {draft.inWorkByName !== null && (
        <p className="mt-2">
          <InWorkStamp name={draft.inWorkByName} since={draft.inWorkSince} />
          <span className="ml-2 text-[13px] text-muted">
            отозвать комплект уже нельзя, обратитесь к нему{" "}
            <HelpLink topic="draftWithdrawBlockedByLawyer" />
          </span>
        </p>
      )}

      {/* Отметка «ушёл модели» осмысленна ТОЛЬКО там, где разбор СОСТОЯЛСЯ. Пока он идёт, она
          снята у всех файлов по построению; если он упал — не ушло вообще ничего, и совет
          «проверьте по скану» подсказывал бы, будто остальное модель прочитала. В обоих случаях
          это тревога о том, чего не было (поймано скриншотами состояний). */}
      {parsed(draft.status) && draft.files.some((f) => !f.sentToAi) && (
        <ul className="mt-2 space-y-1 text-[13px] text-overdue-fg">
          {draft.files
            .filter((f) => !f.sentToAi)
            .map((f) => (
              <li key={f.id}>
                «{f.originalName}» на разбор не отправлен
                {f.skipReason === null ? "" : `: ${f.skipReason}`} — проверьте его по скану.
              </li>
            ))}
        </ul>
      )}

      {!withdrawing && (canRetryParsing(draft) || canWithdraw) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {canRetryParsing(draft) && (
            <button
              type="button"
              onClick={() => void onRetry(draft)}
              disabled={busy}
              className={btnSecondary}
            >
              Повторить разбор
            </button>
          )}
          {canWithdraw && (
            <button
              type="button"
              onClick={() => setWithdrawing(true)}
              disabled={busy}
              className={btnSecondary}
            >
              Отозвать комплект
            </button>
          )}
        </div>
      )}

      {withdrawing && (
        <div className="mt-3 rounded-[var(--radius-m)] border border-border p-3">
          <label className="block text-[13px] text-muted" htmlFor={`wd-${draft.id}`}>
            Причина отзыва — она останется в журнале вместо записи (обязательна)
          </label>
          <textarea
            id={`wd-${draft.id}`}
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-1 w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void onWithdraw(draft, reason)}
              disabled={busy || reason.trim() === ""}
              className={btnSecondary}
            >
              Отозвать
            </button>
            <button
              type="button"
              onClick={() => {
                setWithdrawing(false);
                setReason("");
              }}
              disabled={busy}
              className={btnSecondary}
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      {draft.status === "confirmed" && draft.contractId !== null && (
        <Link to={`/contracts/${draft.contractId}`} className={`${btnSecondary} mt-3 inline-block`}>
          Открыть договор
        </Link>
      )}
    </li>
  );
}
